import './kitStyles';
import React from 'react';
import { useAppSelector } from '@/hooks/hooks';
import { useStrings } from '@/i18n';
import { commonStrings } from '@/i18n/dictionaries/common';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import '../../../styles/cabinet-shell.css';

/**
 * WEB-3739 юнит 3 — загрузка страницы, пока догружается её код (запасной вид
 * `Suspense` в `router/index.tsx`). Эталон `design/cabinet-rescin/app.jsx`
 * `pgLoad`, кит фронтендера README «Загрузка страницы при переходе»: та же
 * капля `.spin`, что везде (не крутилка, не полоска, не скелет); появляется
 * через 300 мс за 200 мс, по центру окна (стили `.pg-load` в
 * `styles/cabinet-shell.css`).
 *
 * Свой корень `.ds` с темой человека: стили капли живут под `:where(.ds)`, а
 * `main` кабинета — вне `.ds`. В кабинете корень — `.pg-host` без своего
 * короба (`display: contents`), чтобы фон `.ds` не закрыл меню; на пустом
 * экране (`first` — вход, документы, витрины) корень сам — `div.pg-first`
 * на весь экран с фоном темы.
 */
export interface PageLoadingProps {
  first?: boolean;
}

export function PageLoading({ first }: PageLoadingProps): React.ReactElement {
  const common = useStrings(commonStrings);
  const user = useAppSelector((state) => state.auth?.user);
  const root = useCabinetRootAttributes(user?.theme);
  const load = (
    <div
      className="pg-load"
      role="status"
      aria-busy="true"
      aria-label={common.loading}
      data-testid="page-loading"
    >
      <span className="spin" aria-hidden="true" />
    </div>
  );
  return first ? (
    <div
      {...root}
      className={`${root.className} pg-first`}
      data-testid="page-first"
    >
      {load}
    </div>
  ) : (
    <div {...root} className={`${root.className} pg-host`}>
      {load}
    </div>
  );
}
